import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Space } from 'models';
import { useCurrentPortal } from 'hooks';
import { isDefaultPortal } from 'utils';
import { ThemeProvider, theme } from 'configs/theme-configurator';
import { HTMLParser } from 'containers';
import { CircularProgress, Stack } from '@mui/material';
import {
  PageTitle,
  Select,
  FiltersBar,
  Download,
  ControlWrapper,
  NoData,
} from 'components';
import ServiceButtonForm from 'shared/components/ServiceButtonForm';

import { useStyles } from './style';

interface Props {
  spaces: Space[];
  selectedSpace: Space | null;
  handleChangeSpace: (space: Space) => void;
  fileName: string;
  isValidReport: boolean;
  validatingInProgress: boolean;
}

function buildURL(space: Space | null) {
  return space ? `/reports/space/balanceloan/${space.Id}` : null;
}

type ReportProps = {
  url: string | null;
  isValidReport: boolean;
  validatingInProgress: boolean;
};

function Report(props: ReportProps) {
  const { isValidReport, validatingInProgress, url } = props;

  const { t } = useTranslation();

  if (!url) return null;
  if (validatingInProgress)
    return (
      <Stack alignItems="center" direction="row" justifyContent="center">
        <CircularProgress color="primary" />
      </Stack>
    );

  if (!isValidReport)
    return (
      <Stack alignItems="center" direction="row" justifyContent="center">
        <NoData text={t('#portal.report.balanceloan.message.noloan')} />
      </Stack>
    );

  return <HTMLParser url={url} />;
}

const BalanceLoanDetails = ({
  spaces,
  fileName,
  selectedSpace,
  handleChangeSpace,
  isValidReport,
  validatingInProgress,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const currentPortal = useCurrentPortal();

  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },
      select: classes.selectRoot,
    }),
    [classes]
  );

  const url = buildURL(selectedSpace);

  return (
    <ThemeProvider theme={theme}>
      <Stack alignItems="center" direction="row" mb={2}>
        <PageTitle className={classes.title} />
        {isDefaultPortal(currentPortal) && (
          <ServiceButtonForm>Lainatiedustelu</ServiceButtonForm>
        )}
      </Stack>
      <FiltersBar className={classes.filtersBar}>
        <ControlWrapper className={classes.controlWrapper}>
          <span className={classes.label}>
            {t('#portal.report.bar.option.label.tila')}:
          </span>
          <Select
            value={selectedSpace}
            options={spaces}
            onSelect={handleChangeSpace}
            labelField={'Name'}
            valueField={'Id'}
            withButton
            cls={selectCls}
          />
        </ControlWrapper>
        <ControlWrapper>
          <Download
            disabled={!isValidReport}
            name={fileName}
            url={`${url}?download=true`}
          />
        </ControlWrapper>
      </FiltersBar>
      <Report
        isValidReport={isValidReport}
        url={url}
        validatingInProgress={validatingInProgress}
      />
    </ThemeProvider>
  );
};

export default BalanceLoanDetails;
